import React, { useEffect, useState } from 'react';
import {
    LayoutPage,
    MyAccountPageLayoutConfig,
    layoutWidgetDefinitionsById,
    normalizePageLayout,
    getDefaultLayoutForPage,
    isWidgetEnabled,
} from './myAccountLayoutWidgets';

interface MyAccountPageLayoutProps {
    pageId: string;
    pages: LayoutPage[];
    layout?: MyAccountPageLayoutConfig | null;
    routeComponent?: string;
    routeProps?: Record<string, any>;
    userName?: string;
    featureFlags?: Record<string, any>;
    enabledPackages?: string[];
}

const MyAccountPageLayout: React.FC<MyAccountPageLayoutProps> = ({
    pageId,
    pages,
    layout,
    routeComponent,
    routeProps = {},
    userName = 'User',
    featureFlags = {},
    enabledPackages = [],
}) => {
    const [isMobileLayout, setIsMobileLayout] = useState<boolean>(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return false;
        }

        return window.matchMedia('(max-width: 991.98px)').matches;
    });

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return undefined;
        }

        const mediaQuery = window.matchMedia('(max-width: 991.98px)');
        const handleChange = (event: MediaQueryListEvent | MediaQueryList) => {
            setIsMobileLayout(event.matches);
        };

        handleChange(mediaQuery);

        if (typeof mediaQuery.addEventListener === 'function') {
            mediaQuery.addEventListener('change', handleChange);

            return () => {
                mediaQuery.removeEventListener('change', handleChange);
            };
        }

        mediaQuery.addListener(handleChange);

        return () => {
            mediaQuery.removeListener(handleChange);
        };
    }, []);

    const page = pages.find((item) => item.id === pageId) || {
        id: pageId,
        label: pageId,
        routes: routeComponent ? [{ path: '/', component: routeComponent }] : [],
    };

    const fallback = getDefaultLayoutForPage(page);
    const resolved = normalizePageLayout(layout, fallback);
    const resolvedRouteProps = routeProps;
    const layoutMode = resolved.columnLayout || (resolved.columns > 1 ? 'two_equal' : 'one');
    const columnsToRender = isMobileLayout ? [resolved.columnsData.flat()] : resolved.columnsData;

    return (
        <div className="weka-my-account-page-layout">
            <div className="row g-3">
                {columnsToRender.map((column, columnIndex) => {
                    const effectiveLayoutMode = isMobileLayout ? 'one' : layoutMode;
                    const widthClass = effectiveLayoutMode === 'one' ? 'col-12' : 'col-12 col-lg-6';
                    const widthStyle = effectiveLayoutMode === 'two_split_60_40'
                        ? {
                            flex: `0 0 ${columnIndex === 0 ? '60%' : '40%'}`,
                            maxWidth: `${columnIndex === 0 ? '60%' : '40%'}`,
                        }
                        : undefined;
                    const columnClassName = [
                        widthClass,
                        'weka-my-account-page-layout__column',
                        effectiveLayoutMode === 'two_split_60_40'
                            ? `weka-my-account-page-layout__column--split-${columnIndex === 0 ? 'primary' : 'secondary'}`
                            : '',
                    ].filter(Boolean).join(' ');
                    return (
                        <div key={`col-${columnIndex}`} className={columnClassName} style={widthStyle}>
                            {column.map((widget, widgetIndex) => {
                                const definition = layoutWidgetDefinitionsById[widget.id];
                                if (!definition) {
                                    return null;
                                }
                                if (!isWidgetEnabled(widget, featureFlags, enabledPackages)) {
                                    return null;
                                }

                                const content = definition.render(
                                    widget.settings || {},
                                    { userName, routeComponent, routeProps: resolvedRouteProps, featureFlags, enabledPackages }
                                );

                                if (widget.id === 'route_component') {
                                    return (
                                        <div key={`widget-${columnIndex}-${widgetIndex}`} className="mb-3">
                                            {content}
                                        </div>
                                    );
                                }

                                return (
                                    <div key={`widget-${columnIndex}-${widgetIndex}`} className="mb-3">
                                        {content}
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default MyAccountPageLayout;
